import { HttpsProxyAgent } from 'https-proxy-agent';
import HttpService from '../../../services/http-service/http-service';
import {
    ASTUTE,
    BRAND_ATTRIBUTES,
    RTE_TOUCHPOINT_FORD,
    RTE_TOUCHPOINT_LINCOLN,
    SUPPORT_HUB_REGEX,
} from '../../support-constants';
import { SiteMapResponse } from '../../models/siteMapResponse';

const httpsAgent = new HttpsProxyAgent(
    process.env.http_proxy || 'http://internet.ford.com:83'
);

export default class RteService {
    public async rtePrivilegeContentCallByTopicId(topicId: number, sessionID?: string) {
        return await HttpService.post(
            `${ASTUTE.BASE_URL_RTE}/privilege/content`,
            {
                sessionID,
                privilegedArea: ASTUTE.PRIVILEGED_AREA_WEB_ID,
                intent: topicId.toString(),
            },
            {
                httpsAgent,
                proxy: false,
            }
        )
            .then(response => {
                return response.data;
            })
            .catch(e => {
                console.error(
                    `redisCallByTopicId - RTE Upstream Failure - ${e.message} for topic Id - ${topicId}`
                );
                throw new Error(
                    `redisCallByTopicId - RTE Upstream Failure - ${e.message} for topic Id - ${topicId}`
                );
            });
    }

    public async getSuggestedResultsElastic(
        currentSearchTerm: string,
        sessionID: string
    ) {
        if (sessionID) {
            return await HttpService.post(
                `${ASTUTE.BASE_URL_RTE}/autocomplete`,
                {
                    sessionID,
                    utterance: currentSearchTerm,
                    suggestionCount: '10',
                },
                { httpsAgent, proxy: false }
            )
                .then(result => {
                    let suggestions = result.data.suggestions;
                    suggestions = suggestions.map((item: string) =>
                        item.replace(SUPPORT_HUB_REGEX.ALL_MARKUP_TAGS, '')
                    );
                    return suggestions;
                })
                .catch(e => {
                    console.error(
                        `RTE Get suggested results failure, couldn't get autocomplete: ${e.message}`
                    );
                    throw new Error(
                        `RTE Get suggested results failure, couldn't get autocomplete: ${e.message}`
                    );
                });
        }
    }
    public async getSessionIdForSearch(
        languageRegionCode: string,
        brand: string
    ): Promise<string> {
        languageRegionCode =
            languageRegionCode === 'fr-lu' ? 'fr-be' : languageRegionCode;
        return await HttpService.post(
            `${ASTUTE.BASE_URL_RTE}/init`,
            {
                market: languageRegionCode,
                clientId: brand,
            },
            { httpsAgent, proxy: false }
        )
            .then(value => {
                return value.data.sessionID;
            })

            .catch(e => {
                console.error(
                    `getSessionIdForSearch - RTE Upstream Failure - ${e.message} for supportedMarket - ${languageRegionCode} and brand - ${brand}`
                );
            });
    }
    public async rteArticleSearch(currentSearchTerm: string, sessionID: string) {
        if (sessionID) {
            return await HttpService.post(
                `${ASTUTE.BASE_URL_RTE}/ask`,
                {
                    sessionID,
                    requestUtterance: currentSearchTerm,
                },
                { httpsAgent, proxy: false }
            )
                .then(result => {
                    return result.data;
                })
                .catch(e => {
                    console.error(
                        `rteArticleSearch - RTE Upstream Failure - ${e.message} for supportedMarket - ${currentSearchTerm}`
                    );
                    throw new Error(
                        `rteArticleSearch - RTE Upstream Failure - ${e.message} for supportedMarket - ${currentSearchTerm}`
                    );
                });
        }
    }

    public async sendRteArticleFeedback(
        sessionID: string,
        topicId: number,
        rating: number,
        comments: string
    ) {
        return HttpService.post(
            `${ASTUTE.BASE_URL_RTE}/rating`,
            {
                sessionID,
                rating: rating,
                comment: comments,
                intent: topicId,
            },
            { httpsAgent, proxy: false }
        )
            .then(result => {
                return result.data;
            })
            .catch(async e => {
                console.error(
                    `sendRteArticleFeedback - RTE Upstream Failure - ${e.message} for supportedMarket - ${topicId}`
                );
                throw new Error(
                    `sendRteArticleFeedback - RTE Upstream Failure - ${e.message} for supportedMarket - ${topicId}`
                );
            });
    }

    public async getSiteMap(
        pageNumber: number,
        supportedMarket: string,
        brand: string,
        pageSize?: number
    ): Promise<SiteMapResponse> {
        supportedMarket =
            supportedMarket === 'fr-lu' ? 'fr-be' : supportedMarket;
        const touchPointId = BRAND_ATTRIBUTES[brand].touchpoint;
        const params: { touchpoints: number | string; pageSize?: number } = {
            touchpoints: touchPointId,
        };
        if (pageSize) {
            params.pageSize = pageSize;
        }
        
        return await HttpService.get(
            `${ASTUTE.BASE_URL_RTE_DISCOVERABILITY}/locale/${supportedMarket}/page/${pageNumber}`,
            false,
            {
                params,
                httpsAgent,
                proxy: false,
                headers: { Origin: 'https://www.ford.com' },
            }
        )
            .then(response => {
                return response.data;
            })
            .catch(e => {
                console.error(
                    `getSiteMap - RTE Upstream Failure - ${e.message} for supportedMarket - ${supportedMarket}`
                );
                throw new Error(
                    `getSiteMap - RTE Upstream Failure - ${e.message} for supportedMarket - ${supportedMarket}`
                );
            });
    }

        public async getCategoryBbt(
        categoryId: number,
        locale: string,
        brand: string,
    ) {
        return await HttpService.post(
            `${ASTUTE.BASE_URL_RTE_DISCOVERABILITY}/category-bbt`,
            {
                categoryId: categoryId,
                locale: locale, 
                touchpoint: brand === "ford" ? RTE_TOUCHPOINT_FORD : RTE_TOUCHPOINT_LINCOLN,
            },
            {
                httpsAgent,
                proxy: false,
            }
        )
            .then(response => {
                return response.data;
            })
            .catch(e => {
                console.error(
                    `getCategoryBbt - RTE Upstream Failure - ${e.message}`
                );
                throw new Error(
                    `getCategoryBbt - RTE Upstream Failure - ${e.message}`
                );
            });
    }

    public async getTopicIdByUrlParams(
        locale: string,
        brand: string,
        label: string,
        category: string,
        categoryValue: string
    ): Promise<string> {
        const payload = {
            locale,
            touchpoint: brand === 'ford' ? RTE_TOUCHPOINT_FORD : RTE_TOUCHPOINT_LINCOLN,
            label,
            category,
            categoryValue,
        };
        
        return await HttpService.post(
            `${ASTUTE.BASE_URL_RTE}/topic-id`,
            payload,
            {
                httpsAgent,
                proxy: false,
                headers: { Origin: 'https://www.ford.com' },
            }
        )
            .then(response => {
                return response.data;
            })
            .catch(e => {
                console.error(
                    `getTopicIdByLabel - RTE Upstream Failure - ${e.message} for label - ${label}`
                );
                throw new Error(
                    `getTopicIdByLabel - RTE Upstream Failure - ${e.message} for label - ${label}`
                );
            });
    }

    public async getTopicAttributes(
        brand: string,
        topicId: number,
        languageRegionCode: string
    ) {
        const attributesUrl =
            ASTUTE.BASE_URL_RTE_DISCOVERABILITY + '/attributes/';
        return await HttpService.post(
            attributesUrl + languageRegionCode + '/search',
            {
                filters: [
                    {
                        name: 'topicId',
                        values: [topicId],
                    },
                ],
            },
            { httpsAgent, proxy: false }
        )
            .then(result => {
                return result.data?.responses[0]?.attributes;
            })
            .catch(e => {
                console.error(
                    `getTopicAttributes - RTE Upstream Failure - ${e.message} for supportedMarket - ${languageRegionCode} and Topic Id - ${topicId}`
                );
                throw new Error(
                    `getTopicAttributes - RTE Upstream Failure - ${e.message} for supportedMarket - ${languageRegionCode} and Topic Id - ${topicId}`
                );
            });
    }
}
